import React, { useCallback, useMemo, type JSX } from 'react';
import { useEmployeeProfilePhoenix } from '@pflegenavi/frontend/api-nursing-home';
import { FormControl, MenuItem, Select } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEmployeeMode } from '@pflegenavi/shared-frontend/employee-mode';
import { useLocation } from 'react-router-dom';
import {
  useEmployeeIsNursingHomeAdminOrManager,
  useIsNursingHomeScope,
} from '@pflegenavi/frontend/nursing-home-context';

const routeWhitelists = [
  '/dashboard',
  '/transactions/batch',
  '/transactions/pending',
  '/transactions/submitted',
  '/transactions/unassociated-card-payments',
];

const routeAdminOrManagerWhitelists = [
  ...routeWhitelists,
  '/accounting/card-payments',
];

export const EmployeeModeDropdown = (): JSX.Element | null => {
  const { t } = useTranslation();
  const { selectedEmployeeId, setSelectedEmployeeId } = useEmployeeMode();
  const { data: employeeProfilePhoenix } = useEmployeeProfilePhoenix({});

  const { pathname } = useLocation();

  const employeeIsNursingHomeAdminOrManager =
    useEmployeeIsNursingHomeAdminOrManager();

  // The filtered pages are nursing-home-bound, so the selector has nothing to
  // narrow at legal entity or tenant scope.
  const isNursingHomeScope = useIsNursingHomeScope();

  const handleChange = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      const value = event.target.value as string | undefined;
      setSelectedEmployeeId(value === 'all' ? undefined : value);
    },
    [setSelectedEmployeeId]
  );

  const currentUser = useMemo(() => {
    if (!employeeProfilePhoenix?.user) {
      return null;
    }

    return employeeProfilePhoenix.user;
  }, [employeeProfilePhoenix]);

  // The employee-mode selector requires the new authorization system (roles),
  // so we gate it on `use_new_authorization` instead of the former
  // dev_EmployeeSelector feature flag — hidden on legacy-authorization tenants.
  if (
    !currentUser ||
    !isNursingHomeScope ||
    !employeeProfilePhoenix?.use_new_authorization ||
    !employeeIsNursingHomeAdminOrManager ||
    !routeAdminOrManagerWhitelists.includes(pathname)
  ) {
    return null;
  }

  const employeeName = [currentUser.first_name, currentUser.last_name]
    .filter(Boolean)
    .join(' ');

  return (
    <FormControl
      size="small"
      sx={{
        minWidth: 150,
        '& .MuiOutlinedInput-root': {
          borderRadius: 1,
        },
      }}
    >
      <Select
        value={selectedEmployeeId || 'all'}
        onChange={handleChange as any}
        displayEmpty
        variant="outlined"
      >
        <MenuItem value="all">{t('common.all-employees')}</MenuItem>
        <MenuItem value={currentUser.id}>{employeeName}</MenuItem>
      </Select>
    </FormControl>
  );
};
